import Web3 from "web3";
import ApiRequest from "@/common/lib/apiRequest";
import {
  isMobileDevice,
  isImToken,
  isLedgerSupported,
  isMetamask,
  fetchEthGasPrice,
  isTrezorSupported,
  isPhantomInstalled,
} from "@/common/lib/utils";
import Config from "@/common/config";

const mutationTypes = {
  FETCH_ASSETS: "FETCH_ASSETS",
  SET_ASSETS: "SET_ASSETS",
  FETCH_COINS: "FETCH_COINS",
  SET_COINS: "SET_COINS",
  FETCH_CURRENCIES: "FETCH_CURRENCIES",
  SET_CURRENCIES: "SET_CURRENCIES",
  FETCH_LANGUAGES: "FETCH_LANGUAGES",
  SET_LANGUAGES: "SET_LANGUAGES",
  SET_TOKEN_PRICES: "SET_TOKEN_PRICES",
  FETCH_TIMEZONES: "FETCH_TIMEZONES",
  SET_TIMEZONES: "SET_TIMEZONES",
  SET_GAS_PRICE: "SET_GAS_PRICE",
};

const getWeb3 = () => {
  return new Web3(`${Config.infuraEndpoints}/${Config.infuraId}`);
};

const state = {
  isMobile: isMobileDevice(),
  isImToken: isImToken(),
  isLedgerSupported: isLedgerSupported(),
  isTrezorSupported: isTrezorSupported(),
  isMetamask: isMetamask(),
  isPhantomInstalled: isPhantomInstalled(),
  ethGasPrices: {},
  assetList: {
    fetching: false,
    result: null,
    error: null,
  },
  coinList: {
    fetching: false,
    result: null,
    error: null,
  },
  currencyList: {
    fetching: false,
    result: null,
    error: null,
  },
  timezoneList: {
    fetching: false,
    result: null,
    error: null,
  },
  languageList: {
    fetching: false,
    result: null,
    error: null,
  },
  tokenPrices: {},
  web3: getWeb3,
  maxPriorityFeePerGas: 2.5 * 1e9,
};

const actions = {
  async fetchEthGasPrice({ state, commit }) {
    try {
      const data = await fetchEthGasPrice(Config.etherscanApiKey);
      commit(mutationTypes.SET_GAS_PRICE, data);
      return data;
    } catch (error) {
      return state.ethGasPrices;
    }
  },
  async fetchAssets({ commit }) {
    commit(mutationTypes.FETCH_ASSETS);
    try {
      const res = await ApiRequest.get("/account/coin_networks");
      const coins = res.map(coin => ({
        coin: coin.coin,
        name: coin.coin,
        network: coin.coin_network,
        assetId: coin.id,
        active: true,
      }));
      commit(mutationTypes.SET_ASSETS, { result: coins });
    } catch (error) {
      commit(mutationTypes.SET_ASSETS, { error });
    }
  },
  async fetchCoins({ commit }) {
    commit(mutationTypes.FETCH_COINS);
    try {
      const res = await ApiRequest.get("/account/coins");
      const result = res.map(c => ({
        id: c.id,
        name: c.name,
        fullName: c.full_name,
        logo: c.image,
        contractAddress: c.contract_address,
        isERC20Token: c.is_erc20_token,
        project: c.mapping_id,
      }));
      commit(mutationTypes.SET_COINS, { result });
    } catch (error) {
      commit(mutationTypes.SET_COINS, { error });
    }
  },
  async fetchCurrencies({ commit, state }) {
    if (state.currencyList.result?.length) return state.currencyList.result;
    commit(mutationTypes.FETCH_CURRENCIES);
    try {
      const result = await ApiRequest.get("/admin/supported_currencies");
      commit(mutationTypes.SET_CURRENCIES, { result });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_CURRENCIES, { error });
    }
  },
  async fetchTimezones({ commit, state }) {
    if (state.timezoneList.result?.length) return state.timezoneList.result;
    commit(mutationTypes.FETCH_TIMEZONES);
    try {
      const result = await ApiRequest.get("/admin/supported_timezones");
      commit(mutationTypes.SET_TIMEZONES, { result });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_TIMEZONES, { error });
    }
  },
  async fetchLanguages({ commit, state }) {
    if (state.languageList.result?.length) return state.languageList.result;
    commit(mutationTypes.FETCH_LANGUAGES);
    try {
      const result = await ApiRequest.get("/admin/supported_locales");
      commit(mutationTypes.SET_LANGUAGES, { result });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_LANGUAGES, { error });
    }
  },
  async fetchTokenPrices({ commit, dispatch, state, rootGetters }) {
    try {
      const prices = await ApiRequest.get("/user/exchange-rate");
      const pricesMissing = getPricesMissing(
        prices,
        state.coinList.result,
        rootGetters["projectConfig/projects"]
      );
      if (pricesMissing.length) {
        const data = await Promise.all(
          pricesMissing.map(async item => {
            const price = await dispatch(
              "fetchAssetPriceFromOsmosis",
              item.asset
            );
            return { id: item.id, price };
          })
        );
        data.forEach(item => {
          prices[item.id] = item.price;
        });
      }
      commit(mutationTypes.SET_TOKEN_PRICES, prices);
    } catch (error) {}
  },
  async fetchAssetPriceFromOsmosis({}, asset) {
    try {
      return fetch(`https://api-osmosis.imperator.co/tokens/v2/${asset}`)
        .then(res => res.json())
        .then(res => res?.[0]?.price || 0);
    } catch (error) {
      return 0;
    }
  },
};

const getPricesMissing = (exchangeRates, coinList, projects) => {
  const projectsPriceMap = projects
    .filter(item => item.config?.price?.fromOsmosis)
    .reduce(
      (acc, cur) => ({
        ...acc,
        [cur.config.price.token]: cur.config.price.asset,
      }),
      {}
    );
  return coinList
    .filter(item => !exchangeRates[item.id] && projectsPriceMap[item.name])
    .map(item => ({ id: item.id, asset: projectsPriceMap[item.name] }));
};

const mutations = {
  [mutationTypes.FETCH_ASSETS](state) {
    state.assetList.fetching = true;
    state.assetList.error = null;
  },
  [mutationTypes.SET_ASSETS](state, data) {
    state.assetList = {
      ...state.assetList,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.FETCH_COINS](state) {
    state.coinList.fetching = true;
    state.coinList.error = null;
  },
  [mutationTypes.SET_COINS](state, data) {
    state.coinList = {
      ...state.coinList,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.FETCH_CURRENCIES](state) {
    state.currencyList.fetching = true;
    state.currencyList.error = null;
  },
  [mutationTypes.SET_CURRENCIES](state, data) {
    state.currencyList = {
      ...state.currencyList,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.FETCH_TIMEZONES](state) {
    state.timezoneList.fetching = true;
    state.timezoneList.error = null;
  },
  [mutationTypes.SET_TIMEZONES](state, data) {
    state.timezoneList = {
      ...state.timezoneList,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.FETCH_LANGUAGES](state) {
    state.languageList.fetching = true;
    state.languageList.error = null;
  },
  [mutationTypes.SET_LANGUAGES](state, data) {
    state.languageList = {
      ...state.languageList,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.SET_TOKEN_PRICES](state, data) {
    state.tokenPrices = data;
  },
  [mutationTypes.SET_GAS_PRICE](state, data) {
    state.ethGasPrices = data;
  },
};

const getters = {
  isMobile: state => state.isMobile,
  isImToken: state => state.isImToken,
  isMetamask: state => state.isMetamask,
  isLedgerSupported: state => state.isLedgerSupported,
  isTrezorSupported: state => state.isTrezorSupported,
  isPhantomInstalled: state => state.isPhantomInstalled,
  assetList: state => state.assetList.result || [],
  coinList: state => state.coinList.result || [],
  currencyList: state => state.currencyList.result || [],
  languageList: state => state.languageList.result || [],
  timezoneList: state => state.timezoneList.result || [],
  coinMapNames(state) {
    return (
      state.coinList.result?.reduce(
        (acc, cur) => ({ ...acc, [cur.name]: cur }),
        {}
      ) ?? {}
    );
  },
  coinMapIds(state) {
    return (
      state.coinList.result?.reduce(
        (acc, cur) => ({ ...acc, [cur.id]: cur }),
        {}
      ) ?? {}
    );
  },
  getTokenPriceInUsd: (state, getters) => tokenName => {
    const tokenId =
      getters.coinMapNames[tokenName?.toLowerCase()]?.id ??
      getters.coinMapNames[tokenName]?.id;
    return state.tokenPrices[tokenId] || 0;
  },
  getTokenByName: (state, getters) => tokenName =>
    getters.coinMapNames[tokenName.toLowerCase()] ||
    getters.coinMapNames[tokenName],
  web3: state => state.web3,
  ethGasPrices: state => state.ethGasPrices,
  maxPriorityFeePerGas: state => state.maxPriorityFeePerGas,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
